/**
 * PluggyConnectWidget Component
 *
 * Integra o Pluggy Connect SDK para conectar contas bancárias via Open Banking
 *
 * @docs https://docs.pluggy.ai/docs/connect-widget
 */

import React, { useEffect, useRef, useState } from 'react'

// Declaração global do Pluggy SDK
declare global {
  interface Window {
    PluggyConnect: {
      new (config: PluggyConnectConfig): PluggyConnectInstance
    }
  }
}

interface PluggyConnectConfig {
  connectToken: string
  includeSandbox?: boolean
  onSuccess: (data: { item: { id: string } }) => void
  onError: (error: { message: string; code?: string }) => void
  onClose: () => void
  onEvent?: (event: { type: string; data?: any }) => void
}

interface PluggyConnectInstance {
  init: () => void
  destroy: () => void
}

interface PluggyConnectWidgetProps {
  connectToken: string
  includeSandbox?: boolean
  onSuccess: (data: { item: { id: string } }) => void
  onError: (error: { message: string; code?: string }) => void
  onClose: () => void
}

const PLUGGY_SDK_URL = 'https://cdn.pluggy.ai/pluggy-connect/v2.8.2/pluggy-connect.js'

const PluggyConnectWidget: React.FC<PluggyConnectWidgetProps> = ({
  connectToken,
  includeSandbox = true,
  onSuccess,
  onError,
  onClose
}) => {
  const pluggyRef = useRef<PluggyConnectInstance | null>(null)
  const loadTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const [sdkLoaded, setSdkLoaded] = useState(false)
  const [sdkError, setSdkError] = useState<string | null>(null)
  const [widgetInitialized, setWidgetInitialized] = useState(false)

  // Carregar SDK e inicializar widget
  useEffect(() => {
    let timeoutId: NodeJS.Timeout
    let loadTimeoutId: NodeJS.Timeout

    console.log('🔧 useEffect triggered, connectToken:', connectToken ? 'present' : 'missing')

    // Timeout de segurança de 10 segundos
    loadTimeoutRef.current = setTimeout(() => {
      console.error('⏱️ SDK loading timeout after 10 seconds')
      setSdkError('Timeout ao carregar SDK. Tente novamente.')
      onError({
        message: 'SDK loading timeout',
        code: 'SDK_TIMEOUT'
      })
    }, 10000)

    // Verificar se SDK já está carregado
    if (window.PluggyConnect) {
      console.log('✓ SDK already loaded, initializing immediately...')
      if (loadTimeoutRef.current) clearTimeout(loadTimeoutRef.current)
      setSdkLoaded(true)
      if (connectToken) {
        initializeWidget()
      }
      return () => {
        clearTimeout(timeoutId)
        if (loadTimeoutRef.current) clearTimeout(loadTimeoutRef.current)
      }
    }

    // Carregar SDK do Pluggy via CDN
    console.log('📥 Loading SDK from:', PLUGGY_SDK_URL)
    const script = document.createElement('script')
    script.src = PLUGGY_SDK_URL
    script.async = true
    script.id = 'pluggy-connect-sdk'

    script.onload = () => {
      console.log('✓ Pluggy SDK script loaded')
      if (loadTimeoutRef.current) clearTimeout(loadTimeoutRef.current)

      // Verificar se PluggyConnect está disponível
      if (window.PluggyConnect) {
        console.log('✓ window.PluggyConnect available, initializing...')
        setSdkLoaded(true)
        if (connectToken) {
          initializeWidget()
        }
      } else {
        console.error('✗ Script loaded but window.PluggyConnect is undefined')
        setSdkError('SDK carregado mas não inicializado corretamente.')
        onError({
          message: 'PluggyConnect not available after script load',
          code: 'SDK_INIT_ERROR'
        })
      }
    }

    script.onerror = (error) => {
      console.error('✗ Failed to load Pluggy SDK:', error)
      if (loadTimeoutRef.current) clearTimeout(loadTimeoutRef.current)
      setSdkError('Erro ao carregar SDK do Pluggy. Verifique sua conexão.')
      onError({
        message: 'Failed to load Pluggy SDK',
        code: 'SDK_LOAD_ERROR'
      })
    }

    // Evitar carregar múltiplas vezes
    const existingScript = document.getElementById('pluggy-connect-sdk')
    if (!existingScript) {
      console.log('📝 Appending script to document.body')
      document.body.appendChild(script)
    } else {
      console.log('ℹ️ Script already exists in DOM')
      // Se o script já existe, verificar se já carregou
      if (window.PluggyConnect) {
        if (loadTimeoutRef.current) clearTimeout(loadTimeoutRef.current)
        setSdkLoaded(true)
        if (connectToken) {
          initializeWidget()
        }
      }
    }

    // Cleanup ao desmontar
    return () => {
      clearTimeout(timeoutId)
      if (loadTimeoutRef.current) clearTimeout(loadTimeoutRef.current)
      if (pluggyRef.current) {
        try {
          pluggyRef.current.destroy()
        } catch (error) {
          console.warn('Error destroying Pluggy widget:', error)
        }
      }
    }
  }, [connectToken])

  const initializeWidget = () => {
    console.log('🔧 initializeWidget called')
    console.log('  - window.PluggyConnect exists:', !!window.PluggyConnect)
    console.log('  - connectToken:', connectToken ? connectToken.substring(0, 20) + '...' : 'null')

    if (!window.PluggyConnect) {
      console.error('PluggyConnect not available on window')
      setSdkError('SDK do Pluggy não carregado')
      onError({
        message: 'PluggyConnect SDK not loaded',
        code: 'SDK_NOT_LOADED'
      })
      return
    }

    if (!connectToken) {
      console.error('connectToken is required')
      setSdkError('Token de conexão não fornecido')
      return
    }

    try {
      console.log('🔧 Creating PluggyConnect instance...')

      const config = {
        connectToken,
        includeSandbox,
        onSuccess: (data: any) => {
          console.log('✓ Pluggy Success:', data)
          console.log('  - Item ID:', data.item?.id)
          console.log('  - Dados completos:', JSON.stringify(data, null, 2))
          onSuccess(data)
        },
        onError: (error: { message: string; code?: string }) => {
          console.error('✗ Pluggy Error:', error)
          onError(error)
        },
        onClose: () => {
          console.log('○ Pluggy Widget Closed')
          onClose()
        },
        onEvent: (event: { type: string; data?: any }) => {
          console.log('📡 Pluggy Event:', event)
        }
      }

      console.log('  - Config:', { ...config, connectToken: config.connectToken.substring(0, 20) + '...' })

      pluggyRef.current = new window.PluggyConnect(config)

      console.log('  - Instance created:', !!pluggyRef.current)
      console.log('  - Instance type:', typeof pluggyRef.current)
      console.log('  - Has init method:', typeof pluggyRef.current?.init)

      // Inicializar o widget conforme documentação oficial do Pluggy
      if (pluggyRef.current && typeof pluggyRef.current.init === 'function') {
        console.log('🔧 Calling pluggyConnect.init()...')

        // Cancelar timeout de segurança
        if (loadTimeoutRef.current) {
          clearTimeout(loadTimeoutRef.current)
          console.log('✓ Load timeout cancelled')
        }

        pluggyRef.current.init()
        console.log('✓ Pluggy widget initialized successfully')
        setWidgetInitialized(true)
      } else {
        throw new Error('PluggyConnect instance does not have init() method')
      }
    } catch (error: any) {
      console.error('✗ Error initializing Pluggy widget:', error)
      console.error('  - Error message:', error.message)
      console.error('  - Error stack:', error.stack)
      setSdkError('Erro ao inicializar widget: ' + error.message)
      onError({
        message: error.message || 'Failed to initialize widget',
        code: 'WIDGET_INIT_ERROR'
      })
    }
  }

  // UI de loading enquanto SDK carrega
  if (!sdkLoaded && !sdkError) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mb-4"></div>
        <p className="text-sm text-gray-600">Carregando conexão segura...</p>
        <p className="text-xs text-gray-500 mt-2">
          Conectando com Open Banking Brasil 🔒
        </p>
      </div>
    )
  }

  // UI de erro se SDK falhou ao carregar
  if (sdkError) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4 max-w-md">
          <p className="text-sm text-red-800 font-medium mb-2">
            Erro ao conectar
          </p>
          <p className="text-xs text-red-600">{sdkError}</p>
        </div>
        <button
          onClick={() => {
            setSdkError(null)
            window.location.reload()
          }}
          className="px-4 py-2 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700"
        >
          Tentar Novamente
        </button>
      </div>
    )
  }

  // Widget será injetado aqui pelo SDK do Pluggy
  return (
    <div id="pluggy-connect-container" className="w-full min-h-[400px]">
      {/* O widget do Pluggy será renderizado aqui automaticamente */}
      {!widgetInitialized && (
        <div className="text-center py-8">
          <p className="text-sm text-gray-600">
            Conectando com seu banco...
          </p>
        </div>
      )}
    </div>
  )
}

export default PluggyConnectWidget
